/**
 * Notifications API Service
 * Handles notifications retrieval, marking as read, and deletion
 */

import api from './api';
import type { ApiResponse, PaginationParams } from './api';

export type NotificationType = 'wish_contacted' | 'new_ad_match' | 'system_announcement';

export type Notification = {
  id: string;
  type: NotificationType;
  title: string;
  message: string | null;
  data: Record<string, unknown> | null;
  isRead: boolean;
  createdAt: string;
};

export type NotificationListParams = PaginationParams & {
  unreadOnly?: boolean;
};

export type NotificationListResponse = {
  data: Notification[];
  pagination: {
    total: number;
    unreadCount: number;
    limit: number;
    offset: number;
  };
};

/**
 * Get notifications for current user
 */
export async function getNotifications(
  params: NotificationListParams = {}
): Promise<ApiResponse<NotificationListResponse>> {
  const requestParams: Record<string, string | number> = {};

  if (params.limit) requestParams.limit = params.limit;
  if (params.offset) requestParams.offset = params.offset;
  if (params.unreadOnly) requestParams.unreadOnly = 'true';

  return api.get<NotificationListResponse>('/api/notifications', requestParams);
}

/**
 * Mark notification as read
 */
export async function markNotificationAsRead(
  id: string
): Promise<ApiResponse<{ message: string; id: string }>> {
  const response = await fetch(
    `${import.meta.env.VITE_API_BASE_URL}/api/notifications/${id}/read`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
      },
    }
  );

  return response.json();
}

/**
 * Mark all notifications as read
 */
export async function markAllAsRead(): Promise<ApiResponse<{ message: string; count: number }>> {
  const response = await fetch(
    `${import.meta.env.VITE_API_BASE_URL}/api/notifications/read-all`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
      },
    }
  );

  return response.json();
}

/**
 * Delete notification
 */
export async function deleteNotification(
  id: string
): Promise<ApiResponse<{ message: string; id: string }>> {
  const response = await fetch(
    `${import.meta.env.VITE_API_BASE_URL}/api/notifications/${id}`,
    {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
      },
    }
  );

  return response.json();
}

/**
 * Get unread notification count
 */
export async function getUnreadCount(): Promise<number> {
  const result = await getNotifications({ limit: 1 });
  
  if (result.success && result.data) {
    return result.data.pagination.unreadCount;
  }
  
  return 0;
}
